import React, { useMemo, useState, useCallback } from "react";
import {
  MobileOutlined,
  TabletOutlined,
  LaptopOutlined,
  DesktopOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";
import ScreenSizeModeSelector from "./ScreenSizeModeSelector";

const BREAKPOINTS = [
  { key: "extra_small", shortLabel: "XS", label: "Phone", icon: <MobileOutlined /> },
  { key: "small", shortLabel: "S", label: "Tablet", icon: <TabletOutlined /> },
  { key: "medium", shortLabel: "M", label: "Laptop", icon: <LaptopOutlined /> },
  { key: "large", shortLabel: "L", label: "Desktop", icon: <DesktopOutlined /> },
  { key: "extra_large", shortLabel: "XL", label: "Desk XL", icon: <DesktopOutlined /> },
];

const PRESETS = [1, 2, 3, 4, 5, 6];

function StepperRow({ icon, label, value, onChange }) {
  const numericVal = parseInt(value || 1, 10);
  const hasLabelContent = icon || (label && label.length > 0);

  return (
    <div className="flex items-center gap-3 w-full">
      {hasLabelContent && (
        <div className={`w-24 flex items-center gap-2 ${textColors.primary} ${typography.bodySmall}`}>
          {icon && <span className="text-base">{icon}</span>}
          <span className="font-medium">{label}</span>
        </div>
      )}
      <div className="flex items-center gap-2 flex-1">
        <button
          type="button"
          onClick={() => onChange(Math.max(1, numericVal - 1))}
          className={`w-8 h-8 rounded-lg border ${borders.default} ${surfaces.primary} flex items-center justify-center transition-colors hover:bg-gray-100 dark:hover:bg-dark-700 active:scale-95`}
          aria-label={`Decrease ${label || "value"}`}
        >
          <MinusOutlined className="text-xs" />
        </button>
        <div
          className={`w-10 h-8 rounded-lg border ${borders.default} ${surfaces.secondary} flex items-center justify-center ${typography.bodySmall} font-semibold ${textColors.primary}`}
        >
          {numericVal}
        </div>
        <button
          type="button"
          onClick={() => onChange(numericVal + 1)}
          className={`w-8 h-8 rounded-lg border ${borders.default} ${surfaces.primary} flex items-center justify-center transition-colors hover:bg-gray-100 dark:hover:bg-dark-700 active:scale-95`}
          aria-label={`Increase ${label || "value"}`}
        >
          <PlusOutlined className="text-xs" />
        </button>
      </div>
    </div>
  );
}

function PresetRow({ label, value, onPick }) {
  return (
    <div className="space-y-1">
      <div className={`${textColors.secondary} ${typography.caption}`}>{label}</div>
      <div className="flex gap-1.5 flex-wrap">
        {PRESETS.map((n) => {
          const isSel = Number(value) === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onPick(n)}
              className={`w-8 h-7 rounded-md border text-xs font-medium transition-all ${
                isSel
                  ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
                  : `${borders.default} ${surfaces.tertiary} ${textColors.primary} hover:bg-gray-100 dark:hover:bg-dark-700`
              }`}
              aria-pressed={isSel}
              title={`${n} per row`}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ButtonsPerRowField({ label, help, value, onChange }) {
  const DEFAULT = [1, 2, 4, 5, 6];
  const list = Array.isArray(value) && value.length === 5 ? value : DEFAULT;

  // Convert array to breakpoint object for mode detection
  const breakpointObj = useMemo(() => ({
    extra_small: list[0],
    small: list[1],
    medium: list[2],
    large: list[3],
    extra_large: list[4],
  }), [list]);

  const allEqual = useMemo(() => list.every((v) => v === list[0]), [list]);
  const isTwo = useMemo(() => {
    const mobile = list[0] === list[1];
    const desktop = list[2] === list[3] && list[3] === list[4];
    return mobile && desktop && !allEqual;
  }, [list, allEqual]);

  const [mode, setMode] = useState(allEqual ? "one" : isTwo ? "two" : "all");

  const updateFromBreakpoints = useCallback((partial) => {
    const merged = { ...breakpointObj, ...partial };
    onChange([merged.extra_small, merged.small, merged.medium, merged.large, merged.extra_large]);
  }, [breakpointObj, onChange]);

  const applyUnified = useCallback(
    (v) => onChange([v, v, v, v, v]),
    [onChange]
  );

  const applyMobile = useCallback(
    (v) => {
      const next = [...list];
      next[0] = v;
      next[1] = v;
      onChange(next);
    },
    [list, onChange]
  );

  const applyDesktop = useCallback(
    (v) => {
      const next = [...list];
      next[2] = v;
      next[3] = v;
      next[4] = v;
      onChange(next);
    },
    [list, onChange]
  );

  const handleModeChange = useCallback((newMode) => {
    if (newMode === "one") {
      applyUnified(list[0]);
    } else if (newMode === "two") {
      applyMobile(list[0]);
      applyDesktop(list[2]);
    }
    setMode(newMode);
  }, [list, applyUnified, applyMobile, applyDesktop]);

  return (
    <div className="field-wrapper">
      {label && <div className={`mb-2 ${typography.label} ${textColors.secondary}`}>{label}</div>}
      {help && <div className={`mb-2 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}

      <ScreenSizeModeSelector mode={mode} onChange={handleModeChange} disabled={false} />

      <div className="space-y-3">
        {mode === "one" ? (
          <>
            <StepperRow icon={null} label="" value={list[0]} onChange={applyUnified} />
            <PresetRow label="Quick set" value={list[0]} onPick={applyUnified} />
          </>
        ) : mode === "two" ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <StepperRow icon={<MobileOutlined />} label="Phone" value={list[0]} onChange={applyMobile} />
              <PresetRow label="Phone presets" value={list[0]} onPick={applyMobile} />
            </div>
            <div className="space-y-2">
              <StepperRow icon={<DesktopOutlined />} label="Desktop" value={list[2]} onChange={applyDesktop} />
              <PresetRow label="Desktop presets" value={list[2]} onPick={applyDesktop} />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {BREAKPOINTS.map((bp, idx) => (
              <StepperRow
                key={bp.key}
                icon={bp.icon}
                label={bp.label}
                value={list[idx]}
                onChange={(v) => {
                  const next = [...list];
                  next[idx] = Math.max(1, v);
                  onChange(next);
                }}
              />
            ))}
            <PresetRow
              label="Quick set all"
              value={allEqual ? list[0] : null}
              onPick={applyUnified}
            />
          </div>
        )}
      </div>
    </div>
  );
}
